import {
  useGetBatches,
  getGetBatchesQueryKey,
  getGetBatchesQueryOptions,
} from "@/requests/gen/react-query/batch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { format, parseISO } from "date-fns";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { Badge } from "@/components/ui/badge";
import formatUSD from "@/lib/format-currency";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import {
  BatchStatusEnum,
  PermissionEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { useAuth } from "@/auth/use-auth";
import { useBulkApproveBatches } from "@/requests/gen/react-query/batch-status";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { showAlert } from "@/lib/show-alert";
import { useEffect, useState } from "react";
import {
  createFileRoute,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";

const purchaseOrdersSearchParsers = {
  statuses: parseAsArrayOf(parseAsStringEnum(Object.values(BatchStatusEnum))),
};

const purchaseOrdersSearchAllParsers = {
  ...dataTableSearchParsers,
  ...purchaseOrdersSearchParsers,
};

const loadPurchaseOrdersSearch = createLoader(purchaseOrdersSearchAllParsers);

type PurchaseOrdersSearch = inferParserType<
  typeof purchaseOrdersSearchAllParsers
>;

function purchaseOrdersQueryParams(
  p: PurchaseOrdersSearch,
): Parameters<typeof getGetBatchesQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    statuses: p.statuses ?? undefined,
  };
}

export const Route = createFileRoute(
  "/_private/_warehouse-360/purchase-orders",
)({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetBatchesQueryOptions(
        purchaseOrdersQueryParams(loadPurchaseOrdersSearch(location.searchStr)),
      ),
    ),
  component: PurchaseOrdersPage,
});

interface PurchaseOrderTableProps {
  link?: LinkProps;
  numeric_batch_id: number;
  batch_id: string;
  name: string;
  vendor?: string;
  status?: string;
  order_quantity: number;
  total_price?: number | null;
  ordered_at?: string | null;
  expected_at?: string | null;
}

const baseColumns: ColumnDef<PurchaseOrderTableProps>[] = [
  { header: "Batch ID", accessorKey: "batch_id" },
  { header: "Product", accessorKey: "name" },
  { header: "Vendor", accessorKey: "vendor" },
  {
    header: "Status",
    accessorKey: "status",
    cell: (value) => {
      const status = value.getValue() as string;
      const variant =
        status === "Arrived"
          ? "default"
          : status === "Cancelled"
            ? "destructive"
            : "secondary";
      return <Badge variant={variant}>{status}</Badge>;
    },
  },
  { header: "Order Qty", accessorKey: "order_quantity" },
  { header: "Qty On Hand", accessorKey: "qty_on_hand" },
  { header: "Reorder Level", accessorKey: "reorder_level" },
  {
    header: "Total Price",
    accessorKey: "total_price",
    cell: (value) => formatUSD(value.getValue() as number | null),
  },
  {
    header: "Ordered At",
    accessorKey: "ordered_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) return "";
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
  },
  {
    header: "Expected At",
    accessorKey: "expected_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) return "--";
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
  },
];

const selectColumn: ColumnDef<PurchaseOrderTableProps> = {
  id: "select",
  meta: { skipLink: true },
  header: ({ table }) => (
    <Checkbox
      checked={
        table.getIsAllPageRowsSelected() ||
        (table.getIsSomePageRowsSelected() && "indeterminate")
      }
      onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
      aria-label="Select all"
    />
  ),
  cell: ({ row }) => (
    <Checkbox
      checked={row.getIsSelected()}
      onCheckedChange={(value) => row.toggleSelected(!!value)}
      aria-label="Select row"
    />
  ),
};

function PurchaseOrdersPage() {
  const { authUser } = useAuth();
  const isAdmin = authUser?.permissions.includes(
    PermissionEnum.administrate_legrande,
  );
  const canApprovePO =
    isAdmin ||
    authUser?.permissions.includes(PermissionEnum.approve_warehouse_po);
  const queryClient = useQueryClient();
  const bulkApproveMutation = useBulkApproveBatches();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [queryStates, setQueryStates] = useQueryStates(
    purchaseOrdersSearchAllParsers,
  );

  // Clear selection when page/search/filter params change
  useEffect(() => {
    setRowSelection({});
  }, [
    queryStates.page,
    queryStates.limit,
    queryStates.search,
    queryStates.sort_column,
    queryStates.ascending,
    queryStates.statuses,
  ]);

  const columns = canApprovePO ? [selectColumn, ...baseColumns] : baseColumns;

  const batches = useGetBatches(purchaseOrdersQueryParams(queryStates), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  const tableData: PurchaseOrderTableProps[] | undefined =
    batches.data?.items.map((batch) => ({
      link: linkOptions({
        to: "/batch/$batchId",
        params: { batchId: String(batch.batch_id) },
      }),
      numeric_batch_id: batch.batch_id,
      batch_id: batch.external_batch_id || batch.batch_id.toString(),
      name: batch.warehouse_vendor.warehouse.catalog_product.display_name,
      vendor: batch.warehouse_vendor.vendor?.name,
      status: batch.status,
      order_quantity: batch.order_quantity,
      qty_on_hand: batch.warehouse_vendor.warehouse.quantity_on_hand,
      reorder_level: batch.warehouse_vendor.warehouse.reorder_level,
      total_price: batch.total_price,
      ordered_at: batch.ordered_at,
      expected_at: batch.expected_at,
    }));

  const selectedRows = Object.keys(rowSelection)
    .filter((key) => rowSelection[key])
    .map((key) => tableData?.[Number(key)])
    .filter((row): row is PurchaseOrderTableProps => row !== undefined);

  const selectedBatchIds = selectedRows.map((row) => row.numeric_batch_id);
  const allSelectedAreOpen =
    selectedRows.length > 0 &&
    selectedRows.every((row) => row.status === BatchStatusEnum.Open);

  const handleBulkApprove = () => {
    bulkApproveMutation.mutate(
      { data: { batch_ids: selectedBatchIds } },
      {
        onSuccess: () => {
          toast.success(
            `${selectedBatchIds.length} batch${selectedBatchIds.length > 1 ? "es" : ""} approved.`,
          );
          setRowSelection({});
          queryClient.invalidateQueries({
            queryKey: getGetBatchesQueryKey(),
          });
        },
        onError: (error) => {
          showAlert({
            title: "Error",
            text:
              (error as { response?: { data?: { detail?: string } } })?.response
                ?.data?.detail || "Failed to approve batches.",
            img: "error",
          });
        },
      },
    );
  };

  return (
    <div className="text-left">
      <title>Purchase Orders - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Purchase Orders", link: { to: "/purchase-orders" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Purchase Orders</CardTitle>
          <CardDescription>
            View and manage batch purchase orders by status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={batches.isFetching} />
            <DropdownFilter
              title="Status"
              options={[
                {
                  items: Object.values(BatchStatusEnum).map((status) => ({
                    label: status,
                    value: status,
                  })),
                },
              ]}
              values={queryStates.statuses || []}
              onChange={(values) => setQueryStates({ statuses: values })}
            />
            <ParamClearButton />
            {canApprovePO && selectedBatchIds.length > 0 && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        onClick={handleBulkApprove}
                        disabled={
                          !allSelectedAreOpen || bulkApproveMutation.isPending
                        }
                      >
                        Approve ({selectedBatchIds.length})
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!allSelectedAreOpen && (
                    <TooltipContent>
                      All selected batches must have Open status
                    </TooltipContent>
                  )}
                </Tooltip>
                <Button variant="outline" onClick={() => setRowSelection({})}>
                  Clear Selection
                </Button>
              </>
            )}
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={batches.isLoading}
            isError={batches.isError}
            count={batches.data?.count}
            rowSelection={canApprovePO ? rowSelection : undefined}
            onRowSelectionChange={canApprovePO ? setRowSelection : undefined}
          />
        </CardContent>
      </Card>
    </div>
  );
}
